import React from 'react';
import { motion } from 'framer-motion';
import { Users, FolderGit2, Handshake, HeartHandshake, Target, Check } from 'lucide-react';
import ScrollReveal from '../effects/ScrollReveal';

const reasons = [
  { icon: Users, title: 'Industry Mentors', desc: 'Learn directly from professionals working in top tech companies with real-world experience.', stat: '120+ Experts' },
  { icon: FolderGit2, title: 'Real-Time Projects', desc: 'Build a portfolio with 3–5 production-grade projects during your training period.', stat: '5 Projects' },
  { icon: Handshake, title: 'Placement Until Job', desc: "We don't stop until you get placed no time limits, no excuses, guaranteed support.", stat: '94% Placed' },
  { icon: HeartHandshake, title: 'Personalized Support', desc: 'Dedicated career manager, weekly calls, and guidance tailored to your specific goals.', stat: '1-on-1 Calls' },
  { icon: Target, title: 'Career-Focused', desc: 'Everything we teach is aligned with what the industry actually hires for right now.', stat: '100% Job-Ready' },
];

export default function WhyChooseUs() {
  return (
    <section className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-muted/20 to-transparent pointer-events-none" />
      <div className="absolute inset-0 opacity-[0.015] pointer-events-none"
        style={{ backgroundImage: 'linear-gradient(#0B0B0B 1px,transparent 1px),linear-gradient(90deg,#0B0B0B 1px,transparent 1px)', backgroundSize: '55px 55px' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Text + list */}
          <ScrollReveal variant="fadeLeft">
            <span className="inline-block text-primary text-sm font-bold tracking-widest uppercase mb-3 px-4 py-1.5 rounded-full glass-yellow">The Difference</span>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mt-4 leading-[1.05]">
              Why Choose{' '}
              <span className="text-primary relative">
                XceedEdge
                <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ delay: 0.4, duration: 0.6 }}
                  className="absolute -bottom-1.5 left-0 right-0 h-0.5 bg-primary rounded-full origin-left" />
              </span>
            </h2>
            <p className="text-muted-foreground mt-5 text-lg font-body leading-relaxed max-w-lg">
              We're not just another online course platform. We're a career transformation partner committed to your success.
            </p>

            <div className="mt-8 space-y-4">
              {['No time limits on placement support', 'Dedicated career manager assigned', '1-on-1 mock interviews & feedback', 'Alumni network with 50K+ members'].map((item, i) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                  className="flex items-center gap-3"
                >
                  <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0 shadow-md shadow-primary/30">
                    <Check className="w-3.5 h-3.5 text-primary-foreground" />
                  </div>
                  <span className="text-sm font-semibold text-foreground font-body">{item}</span>
                </motion.div>
              ))}
            </div>

            <motion.a
              href="/contact"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex items-center gap-2 mt-10 px-8 py-4 rounded-2xl bg-primary text-primary-foreground font-bold shadow-2xl shadow-primary/30 hover:bg-primary/90 transition-colors"
            >
              Start Your Journey →
            </motion.a>
          </ScrollReveal>

          {/* Right: Cards */}
          <div className="grid grid-cols-1 gap-4">
            {reasons.map((r, i) => {
              const ReasonIcon = r.icon;
              return (
                <ScrollReveal key={r.title} variant="fadeRight" delay={i * 0.09}>
                  <motion.div
                    whileHover={{ x: -6, scale: 1.02 }}
                    transition={{ type: 'spring', stiffness: 280, damping: 18 }}
                    className="glass rounded-2xl border border-white/80 p-5 flex items-start gap-5 shadow-sm hover:shadow-xl hover:shadow-black/6 transition-all group"
                  >
                    <motion.div
                      whileHover={{ scale: 1.2, rotate: 12 }}
                      className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center flex-shrink-0 shadow-lg shadow-primary/25"
                    >
                      <ReasonIcon className="w-6 h-6 text-primary-foreground" />
                    </motion.div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <h3 className="font-heading font-bold text-sm">{r.title}</h3>
                        <span className="text-xs text-primary font-bold bg-primary/10 px-2.5 py-0.5 rounded-full border border-primary/20 whitespace-nowrap flex-shrink-0 font-body">
                          {r.stat}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground font-body leading-relaxed">{r.desc}</p>
                    </div>
                  </motion.div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}